import { useState } from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import { BodyDefault, BodyXS } from '@/components/atomic/text'
import { maskAccountId } from '@/utils/mask-account-id'

interface AccountIdToggleProps {
  accountId?: string
  size?: 'small' | 'normal'
  disableMasking?: boolean
}

export function AccountIdToggle({
  accountId,
  size = 'normal',
  disableMasking = false,
}: AccountIdToggleProps) {
  const [showAccountId, setShowAccountId] = useState(disableMasking)
  const [copied, setCopied] = useState(false)

  const handleCopyAccountId = () => {
    if (accountId) {
      navigator.clipboard.writeText(accountId)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  return (
    <div className="text-text-secondary">
      {/** biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration */}
      <div
        className="flex items-center gap-2 group"
        onClick={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          // Only stop propagation for Enter/Space to match click
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
        }}
      >
        {size === 'small' ? (
          <BodyXS className="font-mono">
            {showAccountId ? accountId || '' : maskAccountId(accountId)}
          </BodyXS>
        ) : (
          <BodyDefault className="font-mono">
            {showAccountId ? accountId || '' : maskAccountId(accountId)}
          </BodyDefault>
        )}

        {!disableMasking && (
          // biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              setShowAccountId((v) => !v)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                setShowAccountId((v) => !v)
              }
            }}
            aria-label={showAccountId ? 'Hide account ID' : 'Show account ID'}
          >
            <Icon
              iconName={
                showAccountId ? IconName.EyeSlashIcon : IconName.EyeIcon
              }
              className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-primary transition-colors cursor-pointer"
              outline
            />
          </div>
        )}
        {/** biome-ignore lint/a11y/useSemanticElements: Bulk suppression as part of biome migration */}
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation()
            handleCopyAccountId()
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              handleCopyAccountId()
            }
          }}
          aria-label={copied ? 'Account ID copied' : 'Copy account ID'}
        >
          <Icon
            iconName={
              copied ? IconName.CheckIcon : IconName.DocumentDuplicateIcon
            }
            outline
            className={`w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 cursor-pointer hover:text-primary transition-colors ${copied ? 'text-green-600' : ''}`}
          />
        </div>
      </div>
    </div>
  )
}
